import FingerprintJS from '@fingerprintjs/fingerprintjs'
import getCurrencies from 'api/currencies'
import demoAuthRegister from 'api/demoAuth'
import loadGeoInfo from 'api/loadGeoInfo'
import { ICurrency } from 'interfaces/ICurrency'
import Cookies from 'js-cookie'
import { useCallback, useEffect, useMemo, useState } from 'react'
import getReferralCookies from 'utils/getReferralCookies'
import useAntifraudSDK from 'utils/hooks/useAntifraudSDK'
import setTokensAndRedirect from 'utils/setTokensAndRedirect'

import Button from 'components/Button'
import classNames from 'classnames'
import styles from './DemoRegisterButton.module.scss'

interface DemoRegisterButtonProps {
  className?: string
  variant?: 'default' | 'large'
  textOnly?: boolean
}

const DemoRegisterButton = (props: DemoRegisterButtonProps) => {
  const { className, variant = 'default', textOnly = false } = props

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [currencies, setCurrencies] = useState<ICurrency[]>([])
  const [isCurrenciesLoading, setIsCurrenciesLoading] = useState(true)
  const { initAntifraudSession } = useAntifraudSDK()

  const buttonClassName = classNames(className, styles.button, styles[variant])

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await getCurrencies()
        const items: ICurrency[] = Object.values(res?.data?.payload?.items) || []
        setCurrencies(items)
      } catch (e) {
        console.error(e)
      } finally {
        setIsCurrenciesLoading(false)
      }
    }
    fetchCurrencies()
  }, [setCurrencies])

  const preferredCurrencyId = useMemo(() => {
    const available = currencies.filter((item) => item.isCanRegistered)
    const findByCode = (code: string) =>
      available.find((opt) => opt.code?.toUpperCase() === code.toUpperCase())
    const usd = findByCode('USD')
    return usd?.id || available[0]?.id
  }, [currencies])

  const resolveGeoCurrencyId = useCallback(async () => {
    try {
      const res = await loadGeoInfo()
      const geo = res.data.payload.code
      const available = currencies.filter((item) => item.isCanRegistered)
      const found = available.find((option: any) => {
        return option.countries?.toUpperCase()?.split(',')?.includes(geo.toUpperCase())
      })
      if (found) return found.id
    } catch (e) {
      console.error(e)
    }
    return preferredCurrencyId || null
  }, [currencies, preferredCurrencyId])

  const onClick = useCallback(async () => {
    if (isLoading || isCurrenciesLoading) return
    setIsLoading(true)
    setError('')
    try {
      const sessionId = await initAntifraudSession(`demo-register-${Date.now()}`, 'demo-register')

      const [fp, currencyId] = await Promise.all([FingerprintJS.load(), resolveGeoCurrencyId()])
      const fpResponse = await fp.get()

      const { hostname } = window.location
      let registrationSourceId: number | string = getReferralCookies()?.registrationSourceId
      if (hostname.includes('binany24.com')) {
        registrationSourceId = 14
      } else if (hostname.includes('binany.com')) {
        registrationSourceId = 13
      } else if (hostname.includes('1binany.com')) {
        registrationSourceId = 15
      } else if (hostname.includes('2binany.com')) {
        registrationSourceId = 16
      } else if (hostname.includes('3binany.com')) {
        registrationSourceId = 17
      } else if (hostname.includes('4binany.com')) {
        registrationSourceId = 18
      } else if (hostname.includes('5binany.com')) {
        registrationSourceId = 19
      } else if (hostname.includes('binany.in')) {
        registrationSourceId = 20
      } else if (hostname.includes('binanyinkenya.com')) {
        registrationSourceId = 21
      } else if (hostname.includes('binanybd.com')) {
        registrationSourceId = 22
      } else if (hostname.includes('binany.ke')) {
        registrationSourceId = 23
      } else if (hostname.includes('binany1.com')) {
        registrationSourceId = 24
      }

      const res = await demoAuthRegister({
        visitorId: fpResponse.visitorId,
        preferredCurrency: currencyId as number,
        registrationSourceId,
        sessionId: sessionId || undefined,
        ...getReferralCookies(),
      })
      const { accessToken, refreshToken } = res.data.payload || {}
      if (accessToken && refreshToken) {
        Cookies.set('justRegisteredOnPromo', 'true')
        localStorage.setItem('justRegisteredOnPromo', 'true')
        await setTokensAndRedirect(accessToken, refreshToken)
      } else {
        setError('Server error')
      }
    } catch (e) {
      setError('Server error')
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, isCurrenciesLoading, resolveGeoCurrencyId, initAntifraudSession])

  return (
    <>
      {textOnly ? (
        <div className={styles.textOnly}>
          {isCurrenciesLoading || isLoading ? 'Loading...' : 'TRY DEMO'}
        </div>
      ) : (
        <Button disabled={isLoading} onClick={onClick} className={buttonClassName}>
          {isCurrenciesLoading || isLoading ? 'Loading...' : 'Try Demo'}
        </Button>
      )}
    </>
  )
}

export default DemoRegisterButton
